import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from 'recharts';
import { formatDay } from '../health/healthFormat';
import { recentPoints } from '../../utils/checkSets';

// A small trend for ONE number field of one set (a `series` of the readings
// endpoint), for the last six months. A single series, so no legend box — the
// card's own title names it; the latest value is printed beside it and each
// point has a hover tooltip. The list of visits below is the table view.
//
// Marks follow the app's chart specs: a 2px deep-teal line, a 10% teal wash, an
// 8px end-dot ringed in white on hover, no gridlines.

const withUnit = (value, unit) => (unit ? `${value} ${unit}` : String(value));

function TrendTooltip({ active, payload, unit }) {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0]?.payload;
    if (!p) return null;
    return (
        <div className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-[11px] shadow-md">
            <p className="font-semibold text-gray-800">{withUnit(p.value, unit)}</p>
            <p className="text-gray-500">{formatDay(p.recordedAt)}</p>
        </div>
    );
}

function ReadingsTrend({ series, now }) {
    const { t } = useTranslation();
    const points = useMemo(() => recentPoints(series, now), [series, now]);
    if (points.length < 2) return null;
    const values = points.map((p) => p.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const pad = (max - min) * 0.15 || Math.abs(max) * 0.05 || 1;
    const latest = points[points.length - 1];
    const testKey = `${series.setId}-${series.key}`;

    return (
        <figure
            data-testid={`readings-trend-${testKey}`}
            className="flex flex-col gap-1 rounded-xl border border-gray-100 bg-white p-3 shadow-sm"
        >
            <figcaption className="flex items-baseline justify-between gap-2">
                <span className="min-w-0 truncate text-xs font-semibold text-gray-700" title={series.setName}>
                    {series.label}
                    <span className="ml-1 font-normal text-gray-400">· {series.setName}</span>
                </span>
                <span data-testid={`readings-trend-latest-${testKey}`} className="shrink-0 text-sm font-bold text-gray-800">
                    {withUnit(latest.value, series.unit)}
                </span>
            </figcaption>
            <div role="img" aria-label={t('checkSets.trend_aria', { label: series.label })} className="h-14 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={points} margin={{ top: 6, right: 6, bottom: 2, left: 6 }}>
                        <XAxis dataKey="t" type="number" domain={['dataMin', 'dataMax']} hide />
                        <YAxis domain={[min - pad, max + pad]} hide />
                        <Tooltip content={<TrendTooltip unit={series.unit} />} cursor={{ stroke: '#C7D6D5', strokeWidth: 1 }} />
                        <Area
                            type="monotone"
                            dataKey="value"
                            stroke="#2D8A8A"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fill="#4ABDBD"
                            fillOpacity={0.1}
                            dot={false}
                            activeDot={{ r: 4, fill: '#2D8A8A', stroke: '#ffffff', strokeWidth: 2 }}
                            isAnimationActive={false}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
            <p className="flex justify-between text-[10px] text-gray-400">
                <span>{formatDay(points[0].recordedAt)}</span>
                <span>{t('checkSets.trend_points', { count: points.length })}</span>
                <span>{formatDay(latest.recordedAt)}</span>
            </p>
        </figure>
    );
}

export default ReadingsTrend;
